// Value <-> string codec for spreadsheet cells.
//
// The whole spreadsheet works in pure strings: every entry field value (for one locale) is
// encoded to a single-line string for display, copy/paste and dirty-tracking, and decoded back
// into the Contentful value shape on save. Keeping cells single-line is what makes block
// copy/paste to and from Excel (tab/newline separated) safe even for multiline content -
// JSON.stringify escapes embedded newlines.
//
// `field` is the content type field definition ({ id, type, localized, linkType?, items? }).
// It must be the definition from the *row's* content type, since the same field id can have a
// different type in a different content type (see columns.ts).

function jsonOneLine(value: any): string {
	try { return JSON.stringify(value) }
	catch { return '' }
}

function parseJson(str: string): any {
	if (str.trim() === '') return undefined
	try { return JSON.parse(str) }
	catch (e: any) { throw new Error('Invalid JSON: ' + (e?.message ?? e)) }
}

function linkObj(id: string, linkType = 'Entry') {
	return { sys: { type: 'Link', linkType, id } }
}

function encodeArray(value: any, field: any): string {
	if (!Array.isArray(value)) return ''
	// reference arrays -> comma separated ids (Excel-friendly; ids never contain commas)
	if (field?.items?.type === 'Link')
		return value.map((v: any) => v?.sys?.id ?? '').filter(Boolean).join(', ')
	// everything else (e.g. symbol/tag arrays) -> JSON, so arbitrary strings round-trip losslessly
	return jsonOneLine(value)
}

function decodeArray(str: string, field: any): any {
	if (str.trim() === '') return undefined
	if (field?.items?.type === 'Link') {
		const ids = str.split(',').map(s => s.trim()).filter(Boolean)
		if (!ids.length) return undefined
		return ids.map(id => linkObj(id, field.items?.linkType))
	}
	return parseJson(str)
}

// Encode a single field/locale value to its canonical single-line cell string.
export function encodeCell(value: any, field: any): string {
	if (value === null || value === undefined) return ''
	switch (field?.type) {
		case 'Symbol':
		case 'Text':
			return typeof value === 'string' ? value : String(value)
		case 'Integer':
		case 'Number':
			return String(value)
		case 'Boolean':
			return value ? 'true' : 'false'
		case 'Date':
			return String(value)
		case 'Link':
			return value?.sys?.id ?? ''
		case 'Array':
			return encodeArray(value, field)
		case 'Location':
			return value?.lat != null ? `${value.lat},${value.lon}` : ''
		case 'Object':
		case 'RichText':
		default:
			return jsonOneLine(value)
	}
}

// Decode a cell string back into the Contentful value shape. Returns `undefined` for empty
// cells so that saveEntry's empty-value cleanup drops them. Throws on malformed input (e.g.
// invalid JSON) so the caller can flag the offending cell instead of persisting garbage.
export function decodeCell(str: string, field: any): any {
	switch (field?.type) {
		case 'Symbol':
		case 'Text':
			return str === '' ? undefined : str
		case 'Integer': {
			if (str.trim() === '') return undefined
			const n = parseInt(str, 10)
			if (Number.isNaN(n)) throw new Error('Not a whole number: ' + str)
			return n
		}
		case 'Number': {
			if (str.trim() === '') return undefined
			const n = Number(str)
			if (Number.isNaN(n)) throw new Error('Not a number: ' + str)
			return n
		}
		case 'Boolean': {
			const t = str.trim().toLowerCase()
			if (t === '') return undefined
			return t === 'true' || t === '1' || t === 'yes'
		}
		case 'Date':
			return str.trim() === '' ? undefined : str.trim()
		case 'Link': {
			const id = str.trim()
			return id ? linkObj(id, field.linkType) : undefined
		}
		case 'Array':
			return decodeArray(str, field)
		case 'Location': {
			if (str.trim() === '') return undefined
			const parts = str.split(',')
			const lat = Number(parts[0]); const lon = Number(parts[1])
			if (parts.length !== 2 || Number.isNaN(lat) || Number.isNaN(lon))
				throw new Error('Expected "lat,lon": ' + str)
			return { lat, lon }
		}
		case 'Object':
		case 'RichText':
		default:
			return parseJson(str)
	}
}
